import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { modeloNegocio, diaHabil } from '../modelo-negocio';
import { plazoDe, nombreDelPlazo } from '../plazo-unidad';
import { cargosDeComercio } from '../cargos-comercio';

/**
 * Condiciones comerciales de un comercio — el módulo más delicado del alta.
 *
 * 🔴 Las condiciones NO se editan: se VERSIONAN. Una comisión y un modelo de
 * riesgo son un acuerdo, y un acuerdo que ya rigió no se puede reescribir sin
 * mentir sobre las liquidaciones que se emitieron con él. Por eso acá no hay
 * un botón de editar: hay historial, versión nueva y aprobación.
 *
 * Los tres estados de una versión, que la pantalla tiene que distinguir:
 *   VIGENTE  rige hoy      (date_end == null)
 *   FUTURA   empieza después de hoy   → es la única que se puede cancelar
 *   CERRADA  ya la reemplazó otra     → queda como historia, no se toca
 *
 * Y dos de MT (legacy, Mario 28-oct):
 *   CARTERA      MT-LEGACY pasada su fecha de fin: sigue liquidando las ventas hechas hasta ese día
 *   REEMPLAZADA  la dejó INACTIVE el guion 74 al crear MT-LEGACY: sí rigió, no es una cancelada
 */
@Component({
  selector: 'app-comercios-condiciones',
  templateUrl: './comercios-condiciones.component.html',
  styleUrls: ['./comercios-condiciones.component.css']
})
export class ComerciosCondicionesComponent implements OnInit {

  codigo = '';
  comercio: any = null;
  versiones: any[] = [];
  loading = true;
  noEncontrado = false;

  /** Éxito y error de las acciones de esta pantalla, EN LA PÁGINA — cero `Swal`. */
  aviso = '';
  error = '';
  accionando = false;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.codigo = this.ruta.snapshot.paramMap.get('code') || '';
    this.cargar();
  }

  // RBAC (2026-10-06): las condiciones van con config.comercios.terms.* (crear versión →
  // terms.create, aprobar/cancelar → terms.approve); el cargo por uso es del comercio (update).
  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios.${accion}`);

  cargar = async (): Promise<void> => {
    this.loading = true;
    this.error = ''; this.aviso = '';
    try {
      const rep = await this._merchant.getTerms(this.codigo);
      this.comercio = rep?.merchant || null;
      this.versiones = rep?.terms || [];
      this.noEncontrado = !this.comercio;
      // El aviso que dejó la pantalla anterior (p. ej., «versión creada»), una sola vez.
      const venia = (history.state && history.state.aviso) || '';
      if (venia) { this.aviso = venia; history.replaceState({ ...history.state, aviso: null }, ''); }
    } catch {
      this.noEncontrado = true;
    } finally {
      this.loading = false;
    }
  };

  // ── clasificación de una versión ────────────────────────────────────────────

  /**
   * Vigente, futura, cerrada o cancelada. Es lo primero que hay que poder ver de un vistazo.
   *
   * 🔴 DOS DEFECTOS CORREGIDOS ACÁ (certificación 2026-09-28):
   *
   * 1. Ignoraba `state`. Cancelar una versión FUTURA la deja `state: 'INACTIVE'` en el
   *    núcleo (`cancelTermsService`), pero como esta función nunca miraba `state`, la fila
   *    seguía leyendo «Futura · Sin aprobar» con sus botones, ni recargando la pantalla —
   *    el dato que faltaba no era del servidor, era de acá.
   * 2. `date_end` truthy = CERRADA, sin mirar si esa fecha ya pasó. Al crear una versión
   *    futura, el núcleo le pone a la vigente `date_end = fecha de inicio de la nueva`
   *    (que es una fecha FUTURA): esta función la daba por cerrada un día que todavía no
   *    llegó, y la pantalla decía «sin condición vigente» con la vigente de verdad activa.
   *    Ahora sólo es CERRADA si esa fecha de fin ya pasó.
   */
  vigencia = (v: any): 'VIGENTE' | 'FUTURA' | 'CERRADA' | 'CANCELADA' | 'CARTERA' | 'REEMPLAZADA' => {
    if (v?.state === 'INACTIVE') return this.reemplazo(v) ? 'REEMPLAZADA' : 'CANCELADA';
    const ahora = new Date();
    if (new Date(v?.version?.date_init) > ahora) return 'FUTURA';
    const fin = v?.version?.date_end ? new Date(v.version.date_end) : null;
    // LCF-01 (Mario, 28-oct): MT-LEGACY rige por FECHA DE VENTA. Pasado su fin sigue liquidando todo lo
    // vendido antes, todos los días: decir «Cerrada» era falso.
    if (fin && fin <= ahora) return this.esLegacy(v) ? 'CARTERA' : 'CERRADA';
    return 'VIGENTE';
  };

  /** MT-LEGACY y sus versiones (MT-LEGACY-2, etapa 2). Solo existen en Mundo Total. */
  esLegacy = (v: any): boolean => /^MT-LEGACY(-\d+)?$/.test(v?.version?.code || '');

  /** LCF-05: la entrada que dejó el guion 74 al inactivar una versión vieja. */
  reemplazo = (v: any): any => (v?.history || []).find((h: any) => h && h.guion === 74 && h.evento === 'INACTIVADA') || null;

  /** Hasta cuándo rigió: su fecha de fin o, si el guion 74 la reemplazó sin ponérsela, el día del reemplazo. */
  finDe = (v: any): any => v?.version?.date_end || this.reemplazo(v)?.fecha || null;

  /** LCF-02/03: la cartera anterior, aparte de «Rige hoy». */
  get carteraAnterior(): any {
    return this.versiones.find(v => this.vigencia(v) === 'CARTERA') || null;
  }

  /** La última venta que cubre: su fin es exclusivo (la firma, 00:00), así que es un milisegundo antes. */
  ultimaVenta = (v: any): Date => new Date(new Date(v?.version?.date_end).getTime() - 1);

  get vigente(): any {
    return this.versiones.find(v => this.vigencia(v) === 'VIGENTE') || null;
  }

  aprobada = (v: any): boolean => !!v?.approved_by?.date;

  /** Solo una versión FUTURA se cancela. Si ya rigió, hay liquidaciones detrás. */
  cancelable = (v: any): boolean => this.vigencia(v) === 'FUTURA';

  // ── presentación ────────────────────────────────────────────────────────────

  /**
   * 🔴 2026-09-24: EL TARIFARIO MANDA SOBRE LA COMISIÓN SUELTA — la misma regla que la ficha y la
   * lista. Esto leía sólo `commission.value` y el historial decía 5 % para MT-V3 mientras «Rige
   * hoy» decía 3 %: el 5 % es el campo viejo (y la tarifa de 24 horas); lo que rige es el plazo
   * por omisión. Sin tarifario (esquema viejo) se sigue leyendo la comisión única.
   */
  comision = (v: any): string => {
    const p = this.plazoPorOmision(v);
    if (p) return p.type_calc === 'FIXED' ? `$ ${p.commission_value}` : `${p.commission_value} %`;
    const c = v?.commission;
    if (!c || c.value === null || c.value === undefined) return 'sin definir';
    return c.type_calc === 'PERCENTAGE' ? `${c.value} %` : `$ ${c.value}`;
  };

  /** Un porcentaje sin base no significa nada: viajan pegados. */
  base = (v: any): string =>
    ({
      SALE:      'sobre el monto vendido',
      FINANCED:  'sobre el saldo a financiar',
      COLLECTED: 'sobre lo cobrado',
      SETTLED:   'descontado por liquidación'
    } as any)[v?.commission?.base] || 'sobre el monto vendido';

  /** 1.6b: modelo de negocio y regla de día hábil, con los mismos textos que la ficha. */
  modelo = (v: any): string => modeloNegocio(v);
  diaHabil = (v: any): string => diaHabil(v);

  riesgo = (v: any): string => {
    const r = v?.risk_model;
    const t = ({ NO_RECOURSE: 'Sin recurso', RECOURSE: 'Con recurso', MIXED: 'Mixto' } as any)[r?.type_risk];
    if (!t) return '—';
    if (r.type_risk === 'RECOURSE' && r.recourse_days) return `${t} · ${r.recourse_days} días`;
    if (r.type_risk === 'MIXED' && r.guarantee_fund_pct) return `${t} · fondo ${r.guarantee_fund_pct} %`;
    return t;
  };

  /**
   * 2026-09-24: el historial y «Rige hoy» dicen lo MISMO (antes: «al vender, semanal» contra «al
   * registrarse la venta»). La frecuencia sólo se agrega en el esquema viejo: con tarifario, el
   * período lo pone el plazo, no una frecuencia.
   */
  liquidacion = (v: any): string => {
    if (!v?.settlement) return '—';
    const frec = ({ DAILY: 'diaria', WEEKLY: 'semanal', BIWEEKLY: 'quincenal', MONTHLY: 'mensual' } as any)[v.settlement.frequency];
    return this.liquidacionTexto(v) + (this.esEsquemaViejo(v) && frec ? `, ${frec}` : '');
  };

  /** Cuándo se cobra el cargo fijo. Vacío si no hay cargo: no se muestra un cero. */
  cargoFijo = (v: any): string => {
    const f = v?.flat_fee;
    if (!f || !f.value) return '';
    const cuando = f.charged_with === 'INITIAL' ? 'con la inicial' : 'al liquidar';
    // 2026-09-24: con la moneda. «1 · con la inicial» no decía uno de qué.
    // 2026-10-08: también en porcentaje de la venta.
    return f.type_calc === 'PERCENTAGE' ? `${f.value} % de la venta · ${cuando}` : `USD ${f.value} · ${cuando}`;
  };

  /** El modelo de negocio de la versión (minuta 07-oct). Las viejas no lo traen. */
  modeloNegocio = (v: any): string =>
    ({ COMPRA_DEUDA: 'Cesión de deuda', COBRANZA: 'Servicio de cobranza' } as any)[v?.business_model] || '—';

  /** «2 % · Quincenal»: la comisión con el nombre del plazo que la fija. */
  comisionYPlazo = (v: any): string => {
    const p = this.plazoPorOmision(v) || this.tarifario(v)[0];
    return p ? `${this.comision(v)} · ${this.etiquetaPlazo(p)}` : this.comision(v);
  };

  /** Días entre el inicio y el cierre de una versión cerrada, para la confirmación. */
  diasCubiertos = (v: any): number =>
    Math.max(0, Math.round((new Date(v?.version?.date_end).getTime() - new Date(v?.version?.date_init).getTime()) / 86400000));

  /**
   * 🔴 EL CARGO POR USO EN PAUSA — temporal, para pruebas (Juan, 2026-09-25): «para poder probar
   * débito inmediato, apágame el cobro del dólar… quiero poderlo apagar o prender». Es un interruptor
   * del comercio, no una versión: el monto sigue en la versión que rige. Sólo aplica al cargo que se
   * cobra con la inicial, que es el único que la caja le cobra al cliente.
   */
  get cargoEnPausa(): boolean { return !!this.comercio?.usage_fee_paused; }
  guardandoCargo = false;
  errorCargo = '';

  cambiarCargo = async (cobrar: boolean): Promise<void> => {
    if (this.guardandoCargo) return;
    this.guardandoCargo = true;
    this.errorCargo = '';
    try {
      const rep = await this._merchant.setUsageFee(this.codigo, cobrar);
      this.comercio = { ...this.comercio, ...(rep?.merchant ? {
        usage_fee_paused: rep.merchant.usage_fee_paused,
        usage_fee_paused_at: rep.merchant.usage_fee_paused_at,
        usage_fee_paused_by: rep.merchant.usage_fee_paused_by } : {}) };
    } catch (e: any) {
      this.errorCargo = e?.error?.msg || 'No se pudo cambiar el cargo por uso. Inténtalo de nuevo.';
    } finally {
      this.guardandoCargo = false;
    }
  };

  // ── acciones ────────────────────────────────────────────────────────────────

  nuevaVersion = (): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'condiciones', 'nueva']);
  };

  volver = (): void => { this.router.navigate(['/console/merchants', this.codigo]); };

  /**
   * Aprobar y cancelar se confirman EN LA MISMA FILA, en dos pasos — cero
   * `Swal`, igual que «Descartar» en workflow-detalle. Sólo una fila confirma
   * a la vez.
   */
  confirmando: { code: string; tipo: 'aprobar' | 'cancelar' } | null = null;

  confirmandoEsta = (v: any, tipo: 'aprobar' | 'cancelar'): boolean =>
    this.confirmando?.code === v?.version?.code && this.confirmando?.tipo === tipo;

  pedirAprobar = (v: any): void => {
    this.error = '';
    this.confirmando = { code: v.version.code, tipo: 'aprobar' };
  };

  pedirCancelar = (v: any): void => {
    this.error = '';
    this.confirmando = { code: v.version.code, tipo: 'cancelar' };
  };

  descartarConfirmacion = (): void => { this.confirmando = null; };

  /**
   * Aprobar es una acción aparte del alta, a propósito: quien carga los números
   * no es necesariamente quien acepta el acuerdo.
   */
  aprobar = async (v: any): Promise<void> => {
    if (this.accionando) return;
    this.accionando = true;
    this.error = ''; this.aviso = '';
    try {
      await this._merchant.approveTerms(v._id);
      await this.cargar();
      this.aviso = `Se aprobó ${v.version.code}.`;
    } catch (e: any) {
      this.error = e?.error?.msg || e?.message || 'No se pudo aprobar la versión. Intenta de nuevo en un momento.';
    } finally {
      this.accionando = false;
      this.confirmando = null;
    }
  };

  cancelar = async (v: any): Promise<void> => {
    if (this.accionando) return;
    this.accionando = true;
    this.error = ''; this.aviso = '';
    try {
      const rep = await this._merchant.cancelTerms(v._id);
      await this.cargar();
      this.aviso = rep?.reabierta
        ? `Se canceló ${v.version.code}. Volvió a regir ${rep.reabierta}.`
        : `Se canceló ${v.version.code}.`;
    } catch (e: any) {
      this.error = [e?.error?.msg, e?.error?.detalle].filter(Boolean).join('. ') ||
                   'Solo una versión futura se puede cancelar.';
    } finally {
      this.accionando = false;
      this.confirmando = null;
    }
  };

  // ══ lectura del historial ═══════════════════════════════════════════════════

  /** Cuánto tiempo rigió (o lleva rigiendo) una versión. */
  duracion = (v: any): string => {
    // LCF-07: MT-LEGACY empieza en 2000 a propósito (cubre toda la cartera migrada); su duración no dice nada.
    if (this.esLegacy(v)) return this.vigencia(v) === 'CARTERA' ? 'sigue cobrando' : 'rige hoy';
    const desde = new Date(v?.version?.date_init);
    const hasta = this.finDe(v) ? new Date(this.finDe(v)) : new Date();
    const dias = Math.max(0, Math.round((hasta.getTime() - desde.getTime()) / 86400000));
    if (this.vigencia(v) === 'FUTURA') {
      const faltan = Math.ceil((desde.getTime() - Date.now()) / 86400000); // 12 h para mañana es «1 día», no «hoy»
      return faltan <= 0 ? 'empieza hoy' : `empieza en ${faltan} día${faltan === 1 ? '' : 's'}`;
    }
    const texto = dias === 0 ? 'menos de un día'
      : dias < 31 ? `${dias} día${dias === 1 ? '' : 's'}`
      // 2026-09-25: decía «2 mes(es)» para una versión de 53 días (redondeaba hacia arriba).
      : (() => { const m = Math.floor(dias / 30), r = dias - m * 30;
                 return `${m} ${m === 1 ? 'mes' : 'meses'}${r ? ` y ${r} ${r === 1 ? 'día' : 'días'}` : ''}`; })();
    return this.vigencia(v) === 'VIGENTE' ? `${texto} y sigue` : texto;
  };

  /** La versión que todavía no empezó, si hay una. */
  get futura(): any { return this.versiones.find(v => this.vigencia(v) === 'FUTURA') || null; }

  /**
   * 🔴 Una versión SIN comisión es un comercio al que no se le puede facturar.
   * Pasa de verdad: la versión inicial se siembra sin comisión a propósito,
   * porque el monto es una decisión de negocio que no se inventa.
   */
  sinComision = (v: any): boolean => {
    const c = v?.commission;
    return !c || c.value === null || c.value === undefined;
  };

  get hayVersionSinComision(): boolean {
    return this.versiones.some(v =>
      !['CERRADA', 'CANCELADA', 'REEMPLAZADA'].includes(this.vigencia(v)) && this.sinComision(v));
  }

  /**
   * 🔴 EL ESTADO DE LAS CONDICIONES, EN UNA SOLA PALABRA — rediseño 2026-09-20.
   *
   * La pantalla abría con TRES avisos apilados (la doctrina, la versión sin
   * comisión y la programada) y tenía dos más adentro de la tarjeta. Cinco
   * avisos en una pantalla dejan de advertir, que es exactamente lo que la
   * regla de la cinta prohíbe: una por pantalla, y si hay varias condiciones se
   * resuelven en UN estado.
   *
   * El orden es el del daño:
   *   SIN_VIGENTE  no hay condición que rija  → no se le puede facturar
   *   SIN_COMISION hay una versión sin monto  → tampoco se le puede facturar
   *   SIN_APROBAR  rige algo que nadie aceptó → se puede facturar, pero no debería
   *   PROGRAMADA   viene un cambio con fecha  → es un dato, no una alerta
   *   OK           no hay nada que decidir    → no se dibuja ninguna cinta
   */
  /** 🔴 Un solo lugar decide qué es un proveedor: ausente, null u otro valor = comercio normal. */
  get esProveedor(): boolean { return this.comercio?.business_type === 'PROVEEDOR_SERVICIO'; }
  get estadoCondiciones(): 'SIN_VIGENTE' | 'POR_EMPEZAR' | 'SIN_COMISION' | 'SIN_APROBAR' | 'PROGRAMADA' | 'OK' {
    // Juan, 08-oct: la del alta puede empezar mañana; aprobada y por empezar no es «sin condición».
    if (!this.vigente) return this.futura && this.aprobada(this.futura) ? 'POR_EMPEZAR' : 'SIN_VIGENTE';
    if (this.hayVersionSinComision) return 'SIN_COMISION';
    if (!this.aprobada(this.vigente)) return 'SIN_APROBAR';
    if (this.futura) return 'PROGRAMADA';
    return 'OK';
  }

  // 🔴 REDISEÑO 2026-09-20 — `cobroFijo(v)` y `cargoFijoNumero(v)` se fueron con
  // el bloque «Cargo fijo» suelto, que pintaba el monto y el momento del cobro
  // por segunda vez: el mismo dato ya estaba —y sigue estando— en el par «Cargo
  // fijo» de la sección que rige hoy. Las dos partían `flat_fee` en pedazos
  // para ese bloque: una devolvía «con la inicial»/«al liquidar» y la otra el
  // número suelto. `cargoFijo(v)`, que arma las dos cosas en un renglón, es la
  // que quedó y la que usa la plantilla. Verificado que ninguna plantilla del
  // repositorio nombra a las dos que se fueron.

  /** Quién la creó y quién la aprobó. Un acuerdo sin autor no es un acuerdo. */
  autoria = (v: any): string => {
    const partes: string[] = [];
    // 2026-09-25: dd/mm/aaaa como el resto de la pantalla; decía «31/7/2026» al lado de «23/09/2026».
    const dia = (d: any) => new Date(d).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    if (v?.created_by?.date) partes.push(`creada el ${dia(v.created_by.date)}`);
    if (v?.approved_by?.date) partes.push(`aprobada el ${dia(v.approved_by.date)}`);
    else partes.push('sin aprobar');
    return partes.join(' · ');
  };

  /**
   * Los cargos al comercio de una versión, renglón por renglón (Mario, 27-oct): el cargo por uso y cada
   * concepto. Los mismos para «Rige hoy» y para el detalle de cada versión. La pausa del cargo por uso es
   * del comercio y solo se refleja en la que rige hoy.
   */
  cargos = (v: any): any[] => cargosDeComercio(v, this.cargoEnPausa && v === this.vigente);

  // ── detalle de cada versión (fila desplegable del historial) ────────────────

  /** La versión abierta en el historial. Solo una a la vez: abrir otra cierra la anterior. */
  detalleAbierto: string | null = null;
  estaAbierta = (v: any): boolean => this.detalleAbierto === v?.version?.code;
  alternarDetalle = (v: any): void => {
    this.detalleAbierto = this.estaAbierta(v) ? null : (v?.version?.code || null);
  };

  /** Enter o espacio sobre la fila la abre; sobre un botón de adentro (Aprobar, Cancelar) no. */
  teclaFila = (e: any, v: any): void => {
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    this.alternarDetalle(v);
  };

  /** Cuándo se liquida: el momento, sin repetir los días (van en su propio dato). */
  momentoLiquidacion = (v: any): string =>
    v?.settlement ? (({ ON_COLLECTION: 'a medida que el cliente paga', ON_DUE_DATE: 'después que vence la cuota',
      ON_INSTALLMENT_PAID: 'después que el cliente termina de pagar la cuota' } as any)[v.settlement.moment] || 'al registrarse la venta') : '—';

  /** Días entre el corte y el pago. */
  diasDePago = (v: any): string => {
    const d = Number(v?.settlement?.lag_days) || 0;
    return d ? `${d} ${d === 1 ? 'día' : 'días'} después del corte` : 'sin espera';
  };

  /** Frecuencia del esquema viejo; con tarifario la pone el plazo. */
  frecuencia = (v: any): string => (this.esEsquemaViejo(v) && this.FRECUENCIAS[v?.settlement?.frequency]) || '';

  /** Una parte del desglose del plazo: «2 %», o vacío si la fila no lo trae. */
  porcentaje = (n: any): string => (n === null || n === undefined || n === '' ? '—' : `${n} %`);

  fecha = (d: any): string =>
    d ? new Date(d).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  /** Quién, solo si el núcleo lo trae con nombre (si llega el id suelto no se muestra un código). */
  quien = (u: any): string => (u && typeof u === 'object' ? (u.name || u.email || '') : '');

  // ══ TARIFARIO — lo que faltaba en la vista de consulta ═════════════════════

  /** Los plazos activos, del más rápido al más lento: así se lee un tarifario. */
  tarifario = (v: any): any[] =>
    (v?.settlement?.rates || [])
      .filter((r: any) => (r?.days || r?.term) && r.status !== false)
      .map((r: any) => ({ ...r, days: Number(r.days) || ({ '24H':1,'7D':7,'15D':15,'30D':30 } as any)[r.term] || 7 }))
      .sort((a: any, b: any) => a.days - b.days);

  /** PLZ-03: «1 día», «15 días». Antes: 1 día se leía «24 horas». */
  textoDias = (dias: number): string => nombreDelPlazo({ unidad: 'DIAS', valor: Number(dias) || 0 });

  /** El plazo con su unidad («24 horas», «1 día»): el nombre del negocio o, si no tiene, el que sale del plazo. */
  etiquetaPlazo = (r: any): string => r?.label?.trim() || nombreDelPlazo(plazoDe(r || {}));

  /** La base de un plazo, con su nombre. Cae a la de la condición si la fila no la trae. */
  baseDePlazo = (r: any, v: any): string => {
    const b = r?.base || v?.commission?.base || 'FINANCED';
    return ({ SALE: 'el monto vendido', FINANCED: 'el saldo a financiar',
              COLLECTED: 'lo cobrado', SETTLED: 'descontado por liquidación' } as any)[b] || '—';
  };

  /** El plazo que se aplica si nadie elige otro. */
  plazoPorOmision = (v: any): any => {
    const act = this.tarifario(v);
    return act.find((r: any) => r.is_default) || act[0] || null;
  };

  /**
   * 📍 Una condición sin tarifario es del esquema viejo: una comisión única con
   * una frecuencia. Se dice, en vez de mostrarla como si fuera lo mismo.
   */
  esEsquemaViejo = (v: any): boolean => !this.tarifario(v).length;

  readonly FRECUENCIAS: any = {
    DAILY: 'cada 24 horas', WEEKLY: 'cada 7 días',
    BIWEEKLY: 'cada 15 días', MONTHLY: 'cada mes'
  };

  /** Cobranza de MT · fase 2 (VEN-14): en el historial, bajo «Servicio de cobranza», cuándo se le liquida. */
  momentoCorto = (v: any): string | null => {
    if (v?.business_model !== 'COBRANZA') return null;
    // CPC-26: la tercera, «al terminar de pagar la cuota» (por cuota completa).
    return v?.settlement?.moment === 'ON_INSTALLMENT_PAID' ? 'al terminar de pagar la cuota' : v?.settlement?.moment === 'ON_DUE_DATE' ? 'al vencer la cuota' : 'al cobrar';
  };

  /** Momento y diferimiento, en una línea legible. */
  liquidacionTexto = (v: any): string => {
    const s = v?.settlement;
    if (!s) return '—';
    const momento = s.moment === 'ON_COLLECTION' ? 'a medida que el cliente paga' : s.moment === 'ON_DUE_DATE' ? 'después que vence la cuota'
      : s.moment === 'ON_INSTALLMENT_PAID' ? 'después que el cliente termina de pagar la cuota' : 'al registrarse la venta';
    const lag = s.lag_days ? ` · se paga ${s.lag_days} ${s.lag_days === 1 ? 'día' : 'días'} después del corte` : '';
    return momento + lag;
  };

  /**
   * 🔴 REGLA DE JUAN, 2026-09-28 — «un plazo por versión». Una versión con más
   * de un plazo activo es de ANTES de esta regla: rige el marcado, y se dice.
   */
  variosPlazos = (v: any): boolean => this.tarifario(v).filter(r => r.status !== false).length > 1;
}
